import React from 'react';
import {
  AlertTriangle,
  Check,
  CircleHelp,
  Plus,
  Upload
} from 'lucide-react';

const FOOTER_ACTION_BTN =
  'inline-flex h-10 min-w-[8.75rem] items-center justify-center rounded-md px-5 text-[14px] font-medium leading-none transition-colors box-border';

const ANALYSIS_CONTENT_MAX = 'max-w-[1100px] w-full';
const CARD_GRID_CLASS = 'grid gap-3 grid-cols-[repeat(auto-fill,minmax(260px,1fr))]';

export const MOCK_AI_FOUND_FILES = [
  {
    id: 1,
    category: 'Layout/ Master Plan',
    fileName: 'Site_Plan_GreenHeights.pdf'
  },
  {
    id: 2,
    category: 'Typical Wall Section',
    fileName: 'Boundary_Wall_Section.jpg'
  }
];

export const MOCK_MISSING_DATA = [
  { id: 1, label: 'North wall length', accuracyBoost: '+25% Accuracy' },
  { id: 2, label: 'South wall length', accuracyBoost: '+25% Accuracy' }
];

export const MOCK_BOQ_ROWS = [
  {
    id: 1,
    workType: 'Excavation',
    quantity: '25.08',
    unit: 'm³',
    source: 'Drawing + Answer',
    confidence: 'Medium'
  },
  {
    id: 2,
    workType: 'Brick Masonry',
    quantity: '18.2',
    unit: 'm³',
    source: 'Drawing',
    confidence: 'Low'
  }
];

const SectionHeading = ({ children }) => (
  <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#9CA3AF] mb-3">
    {children}
  </h3>
);

const AiFoundFileCard = ({ category, fileName }) => (
  <div className="h-full rounded-lg border border-[#E5E7EB] bg-white overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
    <div className="flex items-center gap-2 bg-[#DEF4EC] px-3 py-2.5 border-b border-[#C6E9D9]">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#20A273] text-white">
        <Check size={12} strokeWidth={2.5} />
      </span>
      <span className="text-[13px] font-semibold text-[#166534] truncate">{category}</span>
    </div>
    <p className="px-3 py-3 text-[14px] text-[#374151] truncate">{fileName}</p>
  </div>
);

export const BoqAiFoundFilesSection = ({ files = MOCK_AI_FOUND_FILES, onUploadMore }) => (
  <section className={ANALYSIS_CONTENT_MAX}>
    <SectionHeading>What AI found in your files</SectionHeading>
    <div className={CARD_GRID_CLASS}>
      <button
        type="button"
        onClick={onUploadMore}
        className="h-full min-h-[108px] w-full rounded-lg border-2 border-dashed border-[#C5CCD3] bg-white flex flex-col items-center justify-center gap-2 text-[#6B7280] hover:border-[#9AB4BA] hover:bg-[#FAFBFC] transition-colors">
        <span className="flex h-8 w-8 items-center justify-center rounded-full border border-[#D1D5DB] text-[#9CA3AF]">
          <Plus size={18} strokeWidth={1.75} />
        </span>
        <span className="text-[14px] font-medium text-[#4B5563]">Upload more files</span>
      </button>
      {files.map((file) => (
        <div key={file.id} className="w-full min-w-0">
          <AiFoundFileCard category={file.category} fileName={file.fileName} />
        </div>
      ))}
    </div>
  </section>
);

const MissingDataCard = ({ label, accuracyBoost, onAnswerQuestions, onUploadDrawings }) => (
  <div className="rounded-xl border border-[#FECACA] bg-white overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
    <div className="flex items-center justify-between gap-2 bg-[#FEF2F2] px-3 py-2.5 border-b border-[#FECACA]">
      <div className="flex items-center gap-1.5 min-w-0">
        <AlertTriangle size={16} className="shrink-0 text-[#DC2626]" strokeWidth={2} />
        <span className="text-[13px] font-semibold text-[#991B1B] truncate">{label}</span>
      </div>
      <span className="shrink-0 rounded-md bg-white border border-[#FECACA] px-1.5 py-0.5 text-[10px] font-semibold text-[#B91C1C]">
        {accuracyBoost}
      </span>
    </div>
    <div className="grid grid-cols-2 gap-2 p-3">
      <button
        type="button"
        onClick={onAnswerQuestions}
        className="flex min-h-[72px] w-full flex-col items-center justify-center gap-1.5 rounded-lg border border-[#E5E7EB] bg-white px-2 py-3 hover:border-[#D1D5DB] hover:bg-[#F9FAFB] transition-colors">
        <CircleHelp size={20} className="text-[#6B7280]" strokeWidth={1.5} />
        <span className="text-[12px] font-medium text-[#374151] text-center leading-tight">Answer questions</span>
      </button>
      <button
        type="button"
        onClick={onUploadDrawings}
        className="flex min-h-[72px] w-full flex-col items-center justify-center gap-1.5 rounded-lg border border-[#E5E7EB] bg-white px-2 py-3 hover:border-[#D1D5DB] hover:bg-[#F9FAFB] transition-colors">
        <Upload size={20} className="text-[#6B7280]" strokeWidth={1.5} />
        <span className="text-[12px] font-medium text-[#374151] text-center leading-tight">Upload drawings</span>
      </button>
    </div>
  </div>
);

export const BoqMissingDataSection = ({
  items = MOCK_MISSING_DATA,
  onAnswerQuestions,
  onUploadDrawings
}) => (
  <section className={ANALYSIS_CONTENT_MAX}>
    <SectionHeading>Missing data</SectionHeading>
    <div className={CARD_GRID_CLASS}>
      {items.map((item) => (
        <div key={item.id} className="w-full min-w-0">
          <MissingDataCard
            label={item.label}
            accuracyBoost={item.accuracyBoost}
            onAnswerQuestions={() => onAnswerQuestions?.(item)}
            onUploadDrawings={() => onUploadDrawings?.(item)}
          />
        </div>
      ))}
    </div>
  </section>
);

const confidenceClass = (level) => {
  if (level === 'High') return 'text-[#166534]';
  if (level === 'Medium') return 'text-[#B45309]';
  if (level === 'Low') return 'text-[#DC2626]';
  return 'text-[#374151]';
};

export const BoqRoomSpaceTable = ({ rows = [] }) => {
  if (!rows.length) return null;

  return (
    <section className={ANALYSIS_CONTENT_MAX}>
      <SectionHeading>Room &amp; space data extracted</SectionHeading>
      <div className="w-full rounded-xl border border-[#E5E7EB] bg-white overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-[13px]">
            <thead>
              <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
                {['Room', 'Area', 'Dimensions', 'Perimeter', 'Usage', 'Floor', 'Confidence', 'Source'].map(
                  (header) => (
                    <th
                      key={header}
                      className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.04em] text-[#6B7280] whitespace-nowrap">
                      {header}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b border-[#F3F4F6] last:border-b-0">
                  <td className="px-4 py-3.5 font-medium text-[#111827] whitespace-nowrap">{row.roomName}</td>
                  <td className="px-4 py-3.5 font-semibold text-[#111827] whitespace-nowrap">{row.area}</td>
                  <td className="px-4 py-3.5 text-[#4B5563] whitespace-nowrap">{row.dimensions}</td>
                  <td className="px-4 py-3.5 text-[#4B5563] whitespace-nowrap">{row.perimeter}</td>
                  <td className="px-4 py-3.5 text-[#4B5563] whitespace-nowrap">{row.usageType}</td>
                  <td className="px-4 py-3.5 text-[#4B5563] whitespace-nowrap">{row.floor}</td>
                  <td className={`px-4 py-3.5 font-medium whitespace-nowrap ${confidenceClass(row.confidence)}`}>
                    {row.confidence}
                  </td>
                  <td className="px-4 py-3.5 text-[#4B5563] max-w-[180px] truncate">{row.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export const BoqGeneratedTable = ({ rows = [] }) => {
  if (!rows.length) return null;

  return (
    <section className={ANALYSIS_CONTENT_MAX}>
      <SectionHeading>Extracted work type parameters</SectionHeading>
      <div className="w-full rounded-xl border border-[#E5E7EB] bg-white overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
        <div className="w-full overflow-x-auto">
          <table className="w-full table-fixed text-left text-[13px]">
            <thead>
              <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
                {['Work type item', 'Quantity', 'Unit', 'Source', 'Confidence'].map((header) => (
                  <th
                    key={header}
                    className={`px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.04em] text-[#6B7280] ${
                      header === 'Work type item' ? 'w-[28%]' : 'w-[18%]'
                    }`}>
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b border-[#F3F4F6] last:border-b-0">
                  <td className="px-4 py-3.5 font-medium text-[#111827] truncate">{row.workType}</td>
                  <td className="px-4 py-3.5 font-semibold text-[#111827]">{row.quantity}</td>
                  <td className="px-4 py-3.5 text-[#4B5563]">{row.unit}</td>
                  <td className="px-4 py-3.5 text-[#4B5563] truncate">{row.source}</td>
                  <td className={`px-4 py-3.5 font-medium ${confidenceClass(row.confidence)}`}>
                    {row.confidence}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export const BoqAnalysisResults = ({
  onUploadMore,
  onAnswerQuestions,
  aiFoundFiles = [],
  missingData = [],
  boqRows = [],
  roomSpaceRows = []
}) => (
  <div className={`${ANALYSIS_CONTENT_MAX} space-y-4`}>
    {aiFoundFiles.length > 0 && (
      <BoqAiFoundFilesSection files={aiFoundFiles} onUploadMore={onUploadMore} />
    )}
    {missingData.length > 0 && (
      <BoqMissingDataSection
        items={missingData}
        onAnswerQuestions={onAnswerQuestions}
        onUploadDrawings={onUploadMore}
      />
    )}
    <BoqRoomSpaceTable rows={roomSpaceRows} />
    <BoqGeneratedTable rows={boqRows} />
  </div>
);

export const BoqAnalysisFooter = ({
  collapsed,
  accuracy = 82,
  aiValueCount = 0,
  footerNote,
  roomCoverageScore,
  worktypeCoverageScore,
  readyForBoqEngine = false,
  boqGenerated = false,
  onAnswerQuestions,
  onGenerateBoq,
  isGeneratingBoq = false,
  onExportBom,
  isExportingBom = false,
  exportBomDisabled = false,
  showExportBom = false,
  showRegenerateBoq = false,
  onRegenerateBoq,
  isRegeneratingBoq = false,
  extractRequiredError,
  onUploadFiles,
  onReupload
}) => (
  <div
    className={`fixed bottom-0 right-0 z-[5] bg-white border-t border-[#E5E7EB] px-6 md:px-8 py-4 shadow-[0_-2px_8px_rgba(0,0,0,0.04)] transition-all duration-300 ease-in-out ${
      collapsed ? 'left-[76px]' : 'left-64'
    }`}>
    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-semibold text-[#111827] leading-snug">
          Drawing intelligence extracted · {accuracy}% complete
          {boqGenerated ? (
            <span className="text-[#166534]">
              {' '}
              · BOQ generated · {aiValueCount} {aiValueCount === 1 ? 'item' : 'items'}
              {!showExportBom ? ' · BOM created' : ''}
            </span>
          ) : readyForBoqEngine ? (
            <span className="text-[#166534]"> · Ready for BOQ</span>
          ) : (
            <>
              {' '}
              · {aiValueCount} {aiValueCount === 1 ? 'question' : 'questions'} pending
            </>
          )}
        </p>
        <p className="mt-1 text-[13px] text-[#6B7280] leading-snug">
          {extractRequiredError ? (
            <span className="block text-[#B45309]">{extractRequiredError}</span>
          ) : null}
          {footerNote ??
            (readyForBoqEngine
              ? 'Extracted data is ready. Generate BOQ from here, then export BOM.'
              : 'Answer clarification questions to improve BOQ accuracy before generating BOQ.')}
          {roomCoverageScore != null && worktypeCoverageScore != null && (
            <span className="block mt-1">
              Room coverage {roomCoverageScore}% · Work type coverage {worktypeCoverageScore}%
            </span>
          )}
        </p>
      </div>
      <div className="flex shrink-0 items-center justify-end gap-3">
        {extractRequiredError ? (
          <button
            type="button"
            onClick={onUploadFiles}
            className={`${FOOTER_ACTION_BTN} border border-transparent bg-[#2A6A73] text-white hover:bg-[#245a62]`}>
            Upload files
          </button>
        ) : boqGenerated ? (
          showExportBom ? (
            <>
              {showRegenerateBoq ? (
                <button
                  type="button"
                  onClick={onRegenerateBoq}
                  disabled={isRegeneratingBoq || isExportingBom}
                  className={`${FOOTER_ACTION_BTN} border border-[#2A6A73] bg-white text-[#2A6A73] hover:bg-[#E8F4F6] disabled:opacity-50 disabled:cursor-not-allowed`}>
                  {isRegeneratingBoq ? 'Regenerating BOQ…' : 'Regenerate BOQ'}
                </button>
              ) : null}
              <button
                type="button"
                onClick={onReupload}
                disabled={isExportingBom || isRegeneratingBoq}
                className={`${FOOTER_ACTION_BTN} border border-[#2A6A73] bg-white text-[#2A6A73] hover:bg-[#E8F4F6] disabled:opacity-50 disabled:cursor-not-allowed`}>
                Reupload
              </button>
              <button
                type="button"
                onClick={onExportBom}
                disabled={exportBomDisabled || isExportingBom || isRegeneratingBoq}
                className={`${FOOTER_ACTION_BTN} border border-transparent bg-[#2A6A73] text-white hover:bg-[#245a62] disabled:opacity-50 disabled:cursor-not-allowed`}>
                {isExportingBom ? 'Exporting BOM…' : 'Export BOM'}
              </button>
            </>
          ) : null
        ) : readyForBoqEngine ? (
          <button
            type="button"
            onClick={onGenerateBoq}
            disabled={isGeneratingBoq}
            className={`${FOOTER_ACTION_BTN} border border-transparent bg-[#2A6A73] text-white hover:bg-[#245a62] disabled:opacity-50 disabled:cursor-not-allowed`}>
            {isGeneratingBoq ? 'Generating BOQ…' : 'Generate BOQ'}
          </button>
        ) : (
          <button
            type="button"
            onClick={onAnswerQuestions}
            className={`${FOOTER_ACTION_BTN} border border-transparent bg-[#2A6A73] text-white hover:bg-[#245a62]`}>
            Answer questions
          </button>
        )}
      </div>
    </div>
  </div>
);

export const BoqUploadFooter = ({
  collapsed,
  onAnalyzeFiles,
  disabled,
  isLoading,
  loadingLabel = 'Analyzing drawings…'
}) => (
  <div
    className={`fixed bottom-0 right-0 z-[5] bg-white border-t border-[#E5E7EB] px-6 md:px-8 py-4 shadow-[0_-2px_8px_rgba(0,0,0,0.04)] transition-all duration-300 ease-in-out ${
      collapsed ? 'left-[76px]' : 'left-64'
    }`}>
    <div className="flex items-center justify-between gap-4">
      <p className="text-[14px] text-[#4B5563] leading-snug">
        AI will identify each drawing and extract all technical values automatically
      </p>
      <button
        type="button"
        onClick={onAnalyzeFiles}
        disabled={disabled || isLoading}
        className={`${FOOTER_ACTION_BTN} border border-transparent bg-[#2A6A73] text-white hover:bg-[#245a62] shrink-0 disabled:opacity-50 disabled:cursor-not-allowed`}>
        {isLoading ? loadingLabel : 'Analyze Files'}
      </button>
    </div>
  </div>
);
